// frontend/src/pages/admin/TicketCategories.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Input, Popconfirm, Badge, Select, Modal } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import TicketCategoryForm from './TicketCategoryForm';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;

const TicketCategories = () => {
  const [ticketCategories, setTicketCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTicketCategory, setEditingTicketCategory] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [hardDeletingId, setHardDeletingId] = useState(null);

  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchTicketCategories = useCallback(async (triggeredByAction = false) => {
    setLoading(true);
    const loadingKey = 'fetchTicketCategoriesLoading';
    const hideLoading = showLoading(loadingKey, 'Loading ticket categories...');

    try {
      const params = {
        status: filterStatus,
      };

      if (searchText) {
        params.search = searchText;
      }

      const response = await api.get('/api/ticket-categories', { params });
      let fetchedCategories = response.data.data || [];

      if (!Array.isArray(fetchedCategories)) {
        console.warn('TicketCategories.js: API response for ticket categories was not an array. Assuming empty array.');
        fetchedCategories = [];
      }

      setTicketCategories(fetchedCategories);
      hideLoading();

      if (!triggeredByAction) {
        showSuccess('Ticket Categories Loaded', 'Ticket category list fetched successfully!', 'fetchTicketCategoriesSuccess');
      }
    } catch (error) {
      hideLoading();
      console.error('Error fetching ticket categories:', error.response?.data || error.message, error);
      showError('Failed to Fetch Ticket Categories', error.response?.data?.message || 'An unexpected error occurred while fetching ticket categories.', 'fetchTicketCategoriesError');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchText, showLoading, showSuccess, showError]);

  useEffect(() => {
    fetchTicketCategories();
  }, [fetchTicketCategories]);

  const handleAddTicketCategory = () => {
    setEditingTicketCategory(null);
    setIsModalVisible(true);
  };

  const handleEditTicketCategory = (categoryRecord) => {
    setEditingTicketCategory(categoryRecord);
    setIsModalVisible(true);
  };

  const handleDeactivateTicketCategory = async (categoryId, categoryName) => {
    setDeactivatingId(categoryId);
    const loadingKey = `ticketCategoryStatusUpdate-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Deactivating ${categoryName}...`);

    try {
      await api.patch(`/api/ticket-categories/${categoryId}/deactivate`);
      hideLoading();
      showSuccess('Category Status Updated', `${categoryName} was deactivated.`, 'ticketCategoryStatusUpdateSuccess');
      
      fetchTicketCategories(true);      
    } catch (error) {
      hideLoading();
      console.error('Error deactivating ticket category:', error.response?.data || error.message, error);
      showError('Cannot Deactivate Category', error.response?.data?.message || `Failed to deactivate ${categoryName}.`, `deactivateTicketCategoryError-${categoryId}`);
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleActivateTicketCategory = async (categoryId, categoryName) => {
    setActivatingId(categoryId);
    const loadingKey = `ticketCategoryStatusUpdate-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Activating ${categoryName}...`);

    try {
      await api.patch(`/api/ticket-categories/${categoryId}/activate`);
      hideLoading();
      showSuccess('Category Status Updated', `${categoryName} was activated.`, 'ticketCategoryStatusUpdateSuccess');
      
      if (filterStatus === 'inactive') {
        setFilterStatus('active');
      }
      fetchTicketCategories(true);
    } catch (error) {
      hideLoading();
      console.error('Error activating ticket category:', error.response?.data || error.message, error);
      showError('Cannot Activate Category', error.response?.data?.message || `Failed to activate ${categoryName}.`, `activateTicketCategoryError-${categoryId}`);
    } finally {
      setActivatingId(null);
    }
  };

  const handleRestoreTicketCategory = async (categoryId, categoryName) => {
    setRestoringId(categoryId);
    const loadingKey = `ticketCategoryRestore-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Restoring ${categoryName}...`);

    try {
      await api.patch(`/api/ticket-categories/${categoryId}/restore`);
      hideLoading();
      showSuccess('Category Restored', `Ticket category "${categoryName}" restored successfully!`, 'ticketCategoryRestoreSuccess');
      
      setFilterStatus('active');
      fetchTicketCategories(true);
    } catch (error) {
      hideLoading();
      console.error('Error restoring ticket category:', error.response?.data || error.message, error);
      showError('Cannot Restore Category', error.response?.data?.message || `Failed to restore ticket category "${categoryName}".`, `restoreTicketCategoryError-${categoryId}`);
    } finally {
      setRestoringId(null);
    }
  };

  const handleHardDeleteTicketCategory = async (categoryId, categoryName) => {
    setHardDeletingId(categoryId);
    const loadingKey = `ticketCategoryHardDelete-${categoryId}`;
    const hideLoading = showLoading(loadingKey, `Permanently deleting ${categoryName}...`);

    try {
      await api.delete(`/api/ticket-categories/${categoryId}`);
      hideLoading();
      showSuccess('Category Deleted', `Ticket category "${categoryName}" permanently deleted successfully!`, 'ticketCategoryHardDeleteSuccess');
      fetchTicketCategories(true);
    } catch (error) {
      hideLoading();
      console.error('Error hard deleting ticket category:', error.response?.data || error.message, error);
      showError('Cannot Delete Category', error.response?.data?.message || `Failed to hard delete ticket category "${categoryName}".`, `hardDeleteTicketCategoryError-${categoryId}`);
    } finally {
      setHardDeletingId(null);
    }
  };

  const filteredCategories = (ticketCategories || []).filter(category => {
    const matchesSearch = (category.name || '').toLowerCase().includes(searchText.toLowerCase()) || 
                         (category.code || '').toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && category.isActive && !category.deletedAt) ||
                         (filterStatus === 'inactive' && !category.isActive && !category.deletedAt) ||
                         (filterStatus === 'all' && category.deletedAt);
    
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'descend',
      width: '8%',
    },
    {
      title: 'Category Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      width: '20%',
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => (a.code || '').localeCompare(b.code || ''),
      width: '10%',
    },
    {
      title: 'Default Priority',
      dataIndex: 'defaultPriorityId',
      key: 'defaultPriorityId',
      render: (text) => text || 'N/A',
      width: '15%',
    },
    {
      title: 'Response Time (SLA)',
      dataIndex: 'responseTime',
      key: 'responseTime',
      render: (text, record) => record.responseTimeValue ? `${record.responseTimeValue} ${record.responseTimeUnit}(s)` : 'N/A',
      width: '15%',
    },
    {
      title: 'Resolution Time (hours)',
      dataIndex: 'defaultResolutionTimeValue',
      key: 'defaultResolutionTimeValue',
      render: (text) => text || 'N/A',
      width: '15%',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <>
          {record.deletedAt ? (
            <Badge status="default" text="Deleted" />
          ) : (
            <Badge
              status={isActive ? 'success' : 'error'}
              text={isActive ? 'Active' : 'Inactive'}
            />
          )}
        </>
      ),
      width: '10%',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => text ? dayjs(text).format('DD/MM/YYYY HH:mm') : 'N/A',
      sorter: (a, b) => {
        const dateA = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
        const dateB = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
        return dateA - dateB;
      },
      width: '15%',
    },
    {
    title: 'Actions',
    key: 'actions',
    width: '17%',
    render: (_, record) => (
        <Space size="middle">
        <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditTicketCategory(record)}
            aria-label={`Edit ${record.name}`}
            disabled={record.deletedAt}
        />

        {record.deletedAt ? (
            <Popconfirm
            title={`Are you sure you want to restore "${record.name}"?`}
            onConfirm={() => handleRestoreTicketCategory(record.id, record.name)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ loading: restoringId === record.id }}
            >
            <Button type="link" icon={<CheckCircleOutlined />} aria-label={`Restore ${record.name}`} />
            </Popconfirm>
        ) : (
            <>
            {record.isActive ? (
                <Popconfirm
                title={`Are you sure you want to deactivate "${record.name}"?`}
                onConfirm={() => handleDeactivateTicketCategory(record.id, record.name)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ loading: deactivatingId === record.id, danger: true }}
                >
                <Button type="link" danger icon={<CloseCircleOutlined />} aria-label={`Deactivate ${record.name}`} />
                </Popconfirm>
            ) : (
                <Popconfirm
                title={`Are you sure you want to activate "${record.name}"?`}
                onConfirm={() => handleActivateTicketCategory(record.id, record.name)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ loading: activatingId === record.id }}
                >
                <Button type="link" icon={<CheckCircleOutlined />} aria-label={`Activate ${record.name}`} />
                </Popconfirm>
            )}
            </>
        )}

        <Popconfirm
            title={`Are you absolutely sure you want to PERMANENTLY DELETE the category "${record.name}"? This cannot be undone and will fail if tickets are linked. Only use if no longer needed ANYWHERE.`}
            onConfirm={() => handleHardDeleteTicketCategory(record.id, record.name)}
            okText="Yes, Delete Permanently"
            cancelText="No, Keep It"
            okButtonProps={{ danger: true, loading: hardDeletingId === record.id }}
        >
            <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            aria-label={`Hard Delete ${record.name}`}
            />
        </Popconfirm>
        </Space>
    ),
    }

  ];

  return (
    <div className="ticket-categories-page">
      <div className="ticket-categories-header">
        <h2>Ticket Category Management</h2>
        <div className="ticket-categories-actions">
          <Space size="middle">
            <Search
              placeholder="Search categories..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => fetchTicketCategories()}
              style={{ width: 300 }}
            />
            <Select
              defaultValue="active"
              size="large"
              style={{ width: 150 }}
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                fetchTicketCategories();
              }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="all">All Status (Incl. Deleted)</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTicketCategory}
              size="large"
            >
              Add Category
            </Button>
          </Space>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredCategories}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <TicketCategoryForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={() => fetchTicketCategories(true)}
        ticketCategory={editingTicketCategory}
      />
    </div>
  );
};

export default TicketCategories;